// src/utils/pdfTemplate.js

import jsPDF from "jspdf";
import html2canvas from 'html2canvas';

/**
 * 获取设备的每英寸点数（DPI）。
 * 通过创建一个宽度为 1 英寸的临时 div 元素，获取其实际像素宽度作为 DPI 值。
 * @returns {number} 设备的 DPI 值。
 */
export function getDPI() {
    const div = document.createElement('div')
    div.style.cssText = 'height: 1in; left: -100%; position: absolute; top: -100%; width: 1in;'
    document.body.appendChild(div)
    const devicePixelRatio = window.devicePixelRatio || 1,
        dpi = div.offsetWidth * devicePixelRatio;
    return dpi
}

/**
 * 将毫米单位转换为像素单位。
 * @param {number} mm - 要转换的毫米值。
 * @param {number} dpi - 设备的 DPI 值。
 * @returns {number} 转换后的像素值，四舍五入取整。
 */
export function mmToPixel(mm, dpi) {
    let inches = mm / 25.4;
    let pixels = inches * dpi;
    return Math.round(pixels);
}

/**
 * 生成 PDF 报告，将指定 HTML 元素转换为 PDF 文件。
 * @param {number} margin - PDF 页面的上下边距，单位为毫米。
 * @param {string} fileName - 生成的 PDF 文件的名称。
 * @returns {Promise<void>} 一个 Promise，在导出完成或出错时 resolve 或 reject。
 */
async function generateReport(margin, fileName) {
    const element = document.getElementById('preview-content');
    if (!element) {
        console.log('未找到要导出的元素');
        return;
    }

    try {
        const a4WidthMm = 210;
        const a4HeightMm = 297;
        const marginTopBottom = Number(margin);
        const availableHeightMm = a4HeightMm - 2 * marginTopBottom;

        const dpi = getDPI();
        const a4WidthPx = mmToPixel(a4WidthMm, dpi);
        const availableHeightPx = mmToPixel(availableHeightMm, dpi);

        const canvas = await html2canvas(element);
        const ctx = canvas.getContext('2d');
        const pdf = new jsPDF('p', 'mm', 'a4');

        let yOffset = 0;
        const totalHeight = canvas.height;
        const pages = [];

        // 先拆分图片并存储到数组，同时统计总页数
        while (yOffset < totalHeight) {
            let sliceHeight = availableHeightPx;

            if (yOffset + sliceHeight > totalHeight) {
                sliceHeight = totalHeight - yOffset;
            } else {
                while (sliceHeight > 0) {
                    let isTransparent = true;
                    for (let x = 0; x < a4WidthPx; x++) {
                        const pixelData = ctx.getImageData(x, yOffset + sliceHeight, 1, 1).data;
                        if (pixelData[3] > 0) {
                            isTransparent = false;
                            break;
                        }
                    }
                    if (isTransparent) {
                        break;
                    }
                    sliceHeight--;
                }
                if (sliceHeight === 0) {
                    sliceHeight = availableHeightPx;
                }
            }

            const sliceCanvas = document.createElement('canvas');
            sliceCanvas.width = a4WidthPx;
            sliceCanvas.height = sliceHeight;
            const sliceCtx = sliceCanvas.getContext('2d');
            sliceCtx.drawImage(canvas, 0, yOffset, a4WidthPx, sliceHeight, 0, 0, a4WidthPx, sliceHeight);

            const imgData = sliceCanvas.toDataURL('image/png');
            pages.push({imgData, imgHeightInPdf: (sliceHeight * a4WidthMm) / a4WidthPx});

            yOffset += sliceHeight;
        }

        const totalPages = pages.length;

        // 遍历数组添加图片和页码到 PDF
        pages.forEach((page, index) => {
            if (index > 0) {
                pdf.addPage();
            }
            pdf.addImage(page.imgData, 'PNG', 0, marginTopBottom, a4WidthMm, page.imgHeightInPdf);

            // 设置页码样式
            pdf.setFontSize(10);
            // 在页面底部居中添加页码
            pdf.text(`${index + 1} / ${totalPages} `, a4WidthMm / 2, a4HeightMm - 10, {align: 'center'});
        });

        pdf.save(fileName + '.pdf');
    } catch (error) {
        console.log('导出 PDF 时出错！');
    }
}



/**
 * 生成用于导出 PDF 的 HTML 模板
 * @param {string} title - PDF 文档的标题
 * @param {string} content - 渲染后的 Markdown HTML 内容
 * @returns {string} 完整的 HTML 字符串
 */
function generatePdfHtml(title, content) {
  // 将标题中的特殊字符进行 HTML 转义，防止 XSS
  const escapedTitle = title
    .replace(/&/g, "&amp;")
    .replace(/</g, "<")
    .replace(/>/g, ">")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

  return `<!DOCTYPE html>
<html>
<head>
  <title>${escapedTitle}</title>
  <meta charset="utf-8">
  <style>
    :root {
      --primary-gradient-start: #667eea;
      --primary-gradient-end: #764ba2;
      --accent-gradient-start: #4facfe;
      --accent-gradient-end: #00f2fe;
      --text-primary: #2c3e50;
      --text-secondary: #334155;
      --text-tertiary: #64748b;
      --bg-main: #ffffff;
      --bg-content: #ffffff;
      --bg-alt: #f8fafc;
      --border-color: #e2e8f0;
      --shadow-color: rgba(0, 0, 0, 0.08);
    }

    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      max-width: 900px;
      margin: 0 auto;
      padding: 50px 40px;
      line-height: 1.7;
      color: var(--text-secondary);
      background-color: var(--bg-main);
    }

    @media print {
      body {
        padding: 30px 20px;
        max-width: 100%;
      }
    }

    /* Headings */
    h1, h2, h3, h4, h5, h6 {
      color: var(--text-primary);
      margin-top: 30px;
      margin-bottom: 20px;
      font-weight: 700;
      line-height: 1.3;
    }

    h1 {
      font-size: 2.5em;
      border-bottom: 3px solid var(--border-color);
      padding-bottom: 0.4em;
      color: var(--primary-gradient-start);
    }

    h2 {
      font-size: 2em;
      border-bottom: 2px solid var(--border-color);
      padding-bottom: 0.3em;
      color: var(--primary-gradient-end);
    }

    h3 {
      font-size: 1.5em;
      color: var(--accent-gradient-start);
    }

    h4 {
      font-size: 1.25em;
    }

    h5 {
      font-size: 1.1em;
    }

    h6 {
      font-size: 1em;
      color: var(--text-tertiary);
    }

    /* Paragraphs & Text */
    p {
      margin-top: 0;
      margin-bottom: 20px;
      font-size: 17px;
    }

    a {
      color: var(--primary-gradient-start);
      text-decoration: none;
      border-bottom: 2px solid transparent;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      font-weight: 500;
    }

    a:hover {
      border-bottom-color: var(--primary-gradient-start);
      color: var(--primary-gradient-end);
    }

    strong {
      color: var(--text-primary);
      font-weight: 700;
    }

    /* Code */
    /* 基础行内代码样式 */
    code:not(.hljs) {
      padding: 0.3em 0.5em;
      margin: 0 0.2em;
      font-size: 88%;
      background-color: #f1f5f9;
      border-radius: 6px;
      font-family: 'Fira Code', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      color: #d63384;
      border: 1px solid var(--border-color);
      box-shadow: 0 1px 2px var(--shadow-color);
    }

    /* 代码块容器 */
    pre {
      padding: 0; /* Padding 移到内部 */
      margin: 25px 0;
      overflow: auto;
      background-color: var(--bg-alt);
      border-radius: 10px;
      border: 1px solid var(--border-color);
      box-shadow: 0 4px 12px var(--shadow-color);
    }

    /* Highlight.js 代码块样式 */
    pre code.hljs {
      padding: 20px;
      margin: 0;
      font-size: 15px;
      line-height: 1.5;
      background: transparent;
      border: none;
      border-radius: 10px;
      display: block;
      color: #2c3e50; /* Fallback text color */
    }
    /* 确保非 hljs 的 code 在 pre 内也能正常显示（如果需要） */
    pre > code:not(.hljs) {
        padding: 20px;
        margin: 0;
        font-size: 15px;
        line-height: 1.5;
        background: transparent;
        border: none;
        border-radius: 10px;
        display: block;
    }

    /* Blockquotes */
    blockquote {
      padding: 20px 25px;
      color: var(--text-tertiary);
      border-left: 5px solid var(--border-color);
      margin: 0 0 25px 0;
      background-color: var(--bg-alt);
      border-radius: 0 10px 10px 0;
      box-shadow: 0 2px 6px var(--shadow-color);
      font-style: italic;
    }

    /* Lists */
    ul, ol {
      padding-left: 2.5em;
      margin-bottom: 20px;
    }

    li {
      margin-bottom: 10px;
      line-height: 1.7;
    }

    /* Images */
    img {
      max-width: 100%;
      height: auto;
      border-radius: 10px;
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
      margin: 20px 0;
      border: 1px solid var(--border-color);
    }

    /* Tables */
    table {
      display: block;
      width: 100%;
      overflow: auto;
      margin: 25px 0;
      border-collapse: collapse;
      border-radius: 10px;
      box-shadow: 0 4px 12px var(--shadow-color);
    }

    table th {
      font-weight: 700;
      background: linear-gradient(135deg, var(--primary-gradient-start) 0%, var(--primary-gradient-end) 100%);
      color: white;
      padding: 15px;
    }

    table th, table td {
      padding: 15px;
      border: 1px solid var(--border-color);
    }

    table tr:nth-child(2n) {
      background-color: var(--bg-alt);
    }

    /* Horizontal Rule */
    hr {
      border: none;
      height: 2px;
      background: linear-gradient(90deg, transparent, var(--border-color), transparent);
      margin: 40px 0;
    }

    /* Empty State (if present in content) */
    .empty-state {
      text-align: center;
      padding: 50px 25px;
      color: var(--text-tertiary);
      background: var(--bg-alt);
      border-radius: 12px;
      margin: 20px 0;
    }

    .empty-state i {
      font-size: 64px;
      margin-bottom: 20px;
      display: block;
      opacity: 0.7;
    }

    .empty-state p {
      font-size: 17px;
      margin: 15px 0;
      color: var(--text-tertiary);
    }

    .empty-state p:first-of-type {
      font-weight: 600;
      font-size: 19px;
      color: var(--text-secondary);
    }

  </style>
  <!-- 引入 Highlight.js 核心库和 GitHub 主题样式 -->
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.7.0/styles/github.min.css">
  <script src="https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.7.0/highlight.min.js"></script>
  <!-- 引入可能需要的额外语言包 (可选，hljs 会自动检测大部分) -->
  <!-- <script src="https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.7.0/languages/javascript.min.js"></script> -->
</head>
<body>
  <div class="markdown-preview-content">
    ${content}
  </div>
  <script>
    // 确保 DOM 加载完毕后执行代码高亮
    document.addEventListener('DOMContentLoaded', (event) => {
         // 配置 highlight.js (可选)
         // hljs.configure({ languages: ['javascript', 'python', 'html', 'css'] });
         hljs.highlightAll();
    });
    // 有时 DOMContentLoaded 在新窗口中可能触发时机问题，所以也尝试直接执行
    if (document.readyState === 'loading') {
         document.addEventListener('DOMContentLoaded', function() { hljs.highlightAll(); });
    } else {
         hljs.highlightAll();
    }
  </script>
</body>
</html>`;
}


// //带数学公式支持的HTML生成函数
//function generatePdfHtmlWithMath(title, content) {
  // //先使用原有的generatePdfHtml函数
  //let fullHtml = generatePdfHtml(title, content);
  
  // //在</style>标签后添加KaTeX支持
  //const katexSupport = `
  //<!-- KaTeX CSS -->
  //<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.css">
//</head>`;

  // //替换head结束标签，添加KaTeX支持
  //fullHtml = fullHtml.replace('</style>\n</head>', katexSupport);
  
  // //在body结束前添加KaTeX JS和渲染脚本
  //const katexScript = `
  //<!-- KaTeX JS -->
  //<script src="https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.js"></script>
  //<script>
    // //渲染数学公式
    //document.addEventListener('DOMContentLoaded', function() {
      // //渲染行内公式
      //const inlineMath = document.querySelectorAll('.math-inline');
      //inlineMath.forEach(element => {
        //try {
          //katex.render(element.textContent, element, {
            //displayMode: false,
            //throwOnError: false
          //});
        //} catch (error) {
          //console.warn('KaTeX inline rendering error:', error);
        //}
      //});
      
      // //渲染块级公式
      //const displayMath = document.querySelectorAll('.math-display');
      //displayMath.forEach(element => {
        //try {
          //katex.render(element.textContent, element, {
            //displayMode: true,
            //throwOnError: false
          //});
        //} catch (error) {
          //console.warn('KaTeX display rendering error:', error);
        //}
      //});
      
      // //重新执行代码高亮
      //if (typeof hljs !== 'undefined') {
        //hljs.highlightAll();
      //}
    //});
  //</script>
//</body>`;

  // //替换body结束标签
  //fullHtml = fullHtml.replace('</body>', katexScript);
  
  //return fullHtml;
//}

export{
  generatePdfHtml,
  generateReport
}